"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getChartTypeID = void 0;
const lodash_1 = __importDefault(require("lodash"));
const RESTStore_1 = __importDefault(require("./store/RESTStore"));
const StoreError_1 = __importDefault(require("./store/StoreError"));
const constants_1 = require("./constants");
const chart_helpers_1 = require("./util/chart.helpers");
const getDefaultChartType = data => {
  if (data && Object.prototype.hasOwnProperty.call(data, 'data') && data.data.length > 3) {
    return 2;
  }
  if (data && Object.prototype.hasOwnProperty.call(data, 'length') && data.length > 3) {
    return 2;
  }
  return 0;
};
const getChartTypeID = (chartType, data) => {
  const chartTypeIDFromName = constants_1.CHART_TYPES[chartType];
  if (chartTypeIDFromName !== undefined) {
    return chartTypeIDFromName;
  }
  const intTypeFromString = Number.parseInt(chartType, 10);
  if (intTypeFromString >= 0) {
    return intTypeFromString;
  }
  return getDefaultChartType(data);
};
exports.getChartTypeID = getChartTypeID;
const isGeographicalChartType = chartTypeID => chartTypeID === constants_1.CHART_TYPES.Map || chartTypeID === constants_1.CHART_TYPES.MapReactMigration || chartTypeID === constants_1.CHART_TYPES.MapReact || chartTypeID === constants_1.CHART_TYPES.CartogramReact;
class ChartModel {
  constructor(stores) {
    if (Array.isArray(stores)) {
      if (stores.length > 0) {
        this.stores = stores;
      } else {
        this.stores = [new RESTStore_1.default()];
      }
    } else {
      this.stores = [stores || new RESTStore_1.default()];
    }
  }
  setModifier(modifier) {
    this.stores.forEach(store => {
      if (store.modifier) {
        // eslint-disable-next-line
        store.modifier = modifier;
      }
    });
  }
  tryStoreGet(m, adjustment, fips, storeIdx) {
    let i = storeIdx;
    if (i === undefined) {
      i = 0;
    }
    if (i >= this.stores.length) {
      throw new StoreError_1.default(`Could not get dataset ${m.id}${adjustment ? `-${adjustment}` : ''}${fips ? `-${fips}` : ''}`);
    }
    const dataset = this.stores[i].get(m, adjustment, fips);
    if (dataset) {
      return dataset.catch(e => {
        if (e.name === 'StoreError') {
          throw e;
        }
        return this.tryStoreGet(m, adjustment, fips, i + 1);
      });
    }
    return this.tryStoreGet(m, adjustment, fips, i + 1);
  }
  expandStates(metrics) {
    let out = [];
    metrics.forEach(metric => {
      if (Array.isArray(metric)) {
        out.push(this.expandStates(metric));
      } else if (metric.allStates) {
        Object.keys(constants_1.STATE_CODES).filter(sc => sc !== 'US' || metric.showUSA).forEach(fips => {
          out = out.concat({
            metric,
            fips
          });
        });
      } else if (metric.states) {
        metric.states.forEach(state => {
          out = out.concat({
            metric,
            ...state
          });
        });
      } else {
        out.push({
          metric
        });
      }
    });
    return out;
  }
  get(metrics, adjustment, states, source) {
    this.stores.forEach(store => {
      store.load(metrics, adjustment, source);
    });
    const expandedMetrics = lodash_1.default.sortBy(this.expandStates(metrics), metric => {
      if (Array.isArray(metric)) {
        return metric[0].fips;
      }
      return metric.fips;
    }).map(metric => {
      if (Array.isArray(metric)) {
        return metric.filter(_ref => {
          let {
            fips
          } = _ref;
          if (!states || !fips) {
            return true;
          }
          return states.includes(fips);
        });
      }
      return metric;
    }).filter(metric => {
      if (Array.isArray(metric)) {
        // we checked in the previous map
        return metric.length > 0;
      }
      if (!states || !metric.fips) {
        return true;
      }
      return states.includes(metric.fips);
    });
    return (0, chart_helpers_1.metricPromise)(expandedMetrics.map(async expanded => {
      if (Array.isArray(expanded)) {
        const dataSets = await Promise.all(expanded.map(_ref2 => {
          let {
            metric,
            fips
          } = _ref2;
          return this.tryStoreGet(metric, adjustment, fips);
        }));
        return expanded.map((exp, i) => ({
          ...exp.metric,
          metric: dataSets[i]
        }));
      }
      const {
        metric,
        fips
      } = expanded;
      return this.tryStoreGet(metric, adjustment, fips).then(dataset => {
        let resolvedFips = fips;
        if (!fips && !!dataset) {
          if (Object.prototype.hasOwnProperty.call(dataset, 'datasets') && dataset.datasets && dataset.datasets[0]) {
            resolvedFips = dataset.datasets[0].geo_id || dataset.datasets[0].geoId;
          } else {
            resolvedFips = dataset.geo_id ?? dataset.geoId;
          }
        }
        return {
          ...expanded.metric,
          ...expanded,
          metric: dataset,
          fips: resolvedFips
        };
      });
    }));
  }
  getParent(metric, adjustment, source) {
    this.stores.forEach(store => {
      store.load([metric], adjustment, source);
    });
    return (0, chart_helpers_1.parentPromise)(this.tryStoreGet(metric, adjustment).then(dataset => ({
      metric: dataset,
      ...metric
    })));
  }
  getChartData(_ref3, states) {
    let {
      metrics,
      parentID,
      adjustment,
      topMetrics,
      bottomMetrics,
      source,
      chartType
    } = _ref3;
    let flattenedMetrics = metrics;
    // Handle grouped metrics
    if (lodash_1.default.has(lodash_1.default.first(metrics), 'metrics')) {
      flattenedMetrics = lodash_1.default.flatMap(metrics, 'metrics');
    }
    const mapsWithAllStates = lodash_1.default.map(flattenedMetrics, metric => {
      if (metric.allStates === undefined && isGeographicalChartType((0, exports.getChartTypeID)(chartType))) {
        return {
          ...metric,
          allStates: true
        };
      }
      return metric;
    });
    return {
      parent: parentID ? this.getParent({
        id: parentID
      }, adjustment, source) : null,
      data: this.get(mapsWithAllStates, adjustment, states, source),
      topMetrics: topMetrics ? this.get(topMetrics, adjustment, states, source) : null,
      bottomMetrics: bottomMetrics ? this.get(bottomMetrics, adjustment, states, source) : null
    };
  }
}
exports.default = ChartModel;